<template>
  <q-dialog
    :model-value="modelValue"
    :transition-show="transitionShow"
    :transition-hide="transitionHide"
    :class="contentClasses"
    :no-route-dismiss="noRouteDismiss"
    :no-backdrop-dismiss="loading || noBackdropDismiss"
    :no-esc-dismiss="loading || noEscDismiss"
    :persistent="persistent"
    :maximized="isMaximized"
    :seamless="seamless"
    :position="position"
    :full-height="fullHeight"
    no-focus
    @update:model-value="onChangeOpenState"
    @show="onShowDialog"
    @hide="onHideDialog"
    @before-show="$emit('beforeShow')"
    @before-hide="$emit('beforeHide')"
    @escape-key="$emit('escapeKey')"
    @shake="$emit('shake')"
  >
    <q-card
      v-if="cardVisible"
      ref="dialogCard"
      :dark="dark"
      :style="cardStyle"
      :class="strToObj(cardClass, {['bg-' + bgColor]: !!bgColor})"
      class="content-card relative-position"
    >
      <!-- 3-row layout (header, content, footer) -->
      <div
        v-if="!hasCardSlot"
        class="relative-position content-wrapper"
        :class="{'has-min-height': !!minHeight}"
      >
        <div
          :class="strToObj(toolbarClasses, {'without-blur': withoutBlur})"
        >
          <slot name="toolbar">
            <q-toolbar>
              <q-btn
                v-if="!withoutIcon"
                dense
                flat
                round
                :icon="closeIcon"
                :disable="loading"
                @click="onChangeOpenState(false)"
              />
              <page-toolbar-title v-if="title">
                <template v-if="subtitle" #subtitle>
                  {{ subtitle }}
                </template>
                {{ title }}
              </page-toolbar-title>
              <slot name="toolbar-buttons" />
              <help-button
                v-if="!Array.isArray(tags) || (Array.isArray(tags) && tags.length > 0)"
                :filter="!Array.isArray(tags) ? tags : undefined"
              />
            </q-toolbar>
            <slot name="toolbar-after" />
          </slot>
        </div>

        <!-- no-wrap: Quasar's .flex wraps, and a wrapping column sizes itself to its widest
             child's content (an 800px preview) instead of to the dialog -->
        <div class="relative-position" :class="{'flex column no-wrap': flex}">
          <!-- Loading -->
          <q-inner-loading
            :showing="loading"
            :dark="dark"
            style="z-index: 5;"
          >
            <div class="absolute-top column flex-center q-pt-xl">
              <loading-spinner v-if="!$slots.loading" />
              <slot v-else name="loading" />
            </div>
          </q-inner-loading>

          <slot />
        </div>
        <div v-if="hasBottomSlot">
          <slot name="bottom" />
        </div>
      </div>
      <slot
        v-else
        name="card"
      />
    </q-card>
  </q-dialog>
</template>

<script>
import { strToObj } from 'src/helpers/globals'
import { App } from '@capacitor/app'

/**
 * Flexible dialog for showing any type of content
 *
 * SLOTS:
 * - toolbar: override the default toolbar at the top with a custom implementation (sticky)
 * - toolbar-buttons : add buttons to default toolbar at top right
 * ' toolbar-after : append additional content after the toolbar but within the header
 * - default: main content of dialog
 * - bottom: content to display at bottom of dialog (sticky)
 * - card: override the entire card with a custom element (such as containerised QLayout)
 * - loading: content to show in the overlaid loading container
 *
 * EVENTS:
 * default QDialog show,hide events
 */
export default {
  props: {
    // Visibility of dialog
    modelValue: {
      type: Boolean,
      required: true
    },
    // Is loading content. If true, will show a spinner
    loading: {
      type: Boolean,
      default: false
    },
    // Disable backdrop dismiss
    noBackdropDismiss: {
      type: Boolean,
      default: false
    },
    // Disable esc key dismiss
    noEscDismiss: {
      type: Boolean,
      default: false
    },
    // Keep dialog open on route change (DEFAULT TRUE)
    noRouteDismiss: {
      type: Boolean,
      default: true
    },
    // Display the dialog full screen on small screens
    fullscreenMobile: {
      type: Boolean,
      default: false
    },
    // Display the dialog full screen on tablet-sized screens
    fullscreenTablet: {
      type: Boolean,
      default: false
    },
    // Title at top of dialog if slot is not overridden
    title: {
      type: String
    },
    // Subtitle at top of dialog if slot is not overridden
    subtitle: {
      type: String
    },
    // Default width of dialog
    width: {
      type: String,
      default: '500px'
    },
    // Fixed height of dialog
    height: {
      type: String
    },
    // Minimum height of dialog
    minHeight: {
      type: String
    },
    // Maximum height of dialog
    maxHeight: {
      type: String
    },
    // Background color of dialog
    bgColor: {
      type: String
    },
    // Classes applied to the dialog container
    cardClass: {
      type: [String, Object]
    },
    // If a subcomponent requires rendering at a certain point, the entire card can be hidden
    cardVisible: {
      type: Boolean,
      default: true
    },
    // Classes applied to the parent QDialog
    contentClass: {
      type: [String, Object]
    },
    // Change default close icon at top right (if title slot is not overridden)
    closeIcon: {
      type: String,
      default: 'mdi-close'
    },
    // Disable applying automatic blur behind title
    withoutBlur: {
      type: Boolean,
      default: false
    },
    // Prevent dialog from being closed
    persistent: {
      type: Boolean,
      default: false
    },
    // Hide icon at top left
    withoutIcon: {
      type: Boolean,
      default: false
    },
    // Dialog position (Refer to QDialog docs)
    position: {
      type: String,
      default: 'standard'
    },
    // Don't overlay the dialog
    seamless: {
      type: Boolean,
      default: false
    },
    // Force dialog to full available height
    fullHeight: {
      type: Boolean,
      default: false
    },
    // Show transition
    transitionShow: {
      type: String,
      default: 'slide-up'
    },
    // Hide transition
    transitionHide: {
      type: String,
      default: 'slide-down'
    },
    // Force dialog to always be on top of other content and dialogs
    alwaysOnTop: {
      type: Boolean,
      default: false
    },
    // Force the main content area into flex mode
    flex: {
      type: Boolean,
      default: false
    },
    // Bound tags which filter results in the help center
    tags: {
      type: [Array, String],
      default: () => {
        return []
      }
    },
    // Disable re-rendering the Help Center when this dialog is opened
    // Usually this option does not need to be altered.
    // It is a bug with interacting with inputs inside the Help Center if it is not the last DOM on the page
    skipHelpChecks: {
      type: Boolean,
      default: false
    },
    // Force dialog to take entire screen real estate
    maximized: {
      type: Boolean,
      default: false
    },
    // Display the dialog in dark mode
    dark: {
      type: Boolean,
      default: false
    },
    // Allow cancellation callback before dialog is closed
    confirmCancel: {
      type: Function,
      default: null
    },
    // Classes to bind to the top toolbar container
    toolbarClasses: {
      type: [String, Object]
    }
  },
  emits: ['beforeShow', 'beforeHide', 'escapeKey', 'shake', 'update:modelValue', 'show', 'hide'],
  computed: {
    hasBottomSlot () {
      return !!this.$slots.bottom
    },
    hasCardSlot () {
      return !!this.$slots.card
    },
    cardStyle () {
      return {
        'width': '100%',
        'max-width': this.width,
        'height': this.height,
        'min-height': this.minHeight,
        'max-height': !this.maxHeight || !this.maxHeight.includes('px') || parseInt(this.maxHeight) <= this.$q.screen.height ? this.maxHeight : undefined,
        'z-index': 0
      }
    },
    contentClasses () {
      return strToObj(
        this.contentClass,
        {
          'always-on-top': this.alwaysOnTop,
          'fixed-content': true,
          'custom-layout': this.hasCardSlot,
          'fullscreen-mobile': this.fullscreenMobile,
          'fullscreen-tablet': this.fullscreenTablet,
          'maximized': this.maximized
        }
      )
    },
    isMaximized () {
      if (this.maximized) {
        return true
      }
      if (this.fullscreenTablet && this.windowWidth < 1025) {
        return true
      }
      return this.fullscreenMobile ? (this.windowWidth < 768 || this.windowHeight < 501) : false
    }
  },
  watch: {
    modelValue (open) {
      if (!this.noRouteDismiss || !this.$q.platform.is.capacitor) {
        return
      }
      // Quasar's own Back handling skips dialogs that ignore route changes, which is
      // every content dialog by default (noRouteDismiss), so the hardware Back is
      // handled here and does what the dialog's own close control does. Stepping the
      // history back instead navigated the page away from under the dialog.
      if (open) {
        this.backListener = App.addListener('backButton', this.onBackButton)
      }
      else {
        this.removeBackListener()
      }
    }
  },
  methods: {
    strToObj,
    onBackButton () {
      // Only the top-most dialog answers: Quasar may have just closed one stacked
      // above this one (still in the DOM while it animates out), and a content
      // dialog under another content dialog must stay put.
      const dialogs = document.querySelectorAll('.q-dialog')
      const top = dialogs[dialogs.length - 1]
      const card = this.$refs.dialogCard && this.$refs.dialogCard.$el
      if (top && card && !top.contains(card)) {
        return
      }
      this.hide()
    },
    async removeBackListener () {
      if (this.backListener) {
        const listener = await this.backListener
        this.backListener = null
        listener.remove()
      }
    },
    hide () {
      this.onChangeOpenState(false)
    },
    onChangeOpenState (val) {
      if (this.modelValue && !val) {
        this.onConfirmCancel()
      }
      else {
        this.$emit('update:modelValue', val)
      }
    },
    onConfirmCancel () {
      const closeFunc = () => {
        this.$emit('update:modelValue', false)
      }

      if (this.confirmCancel) {
        this.confirmCancel(closeFunc)
      }
      else {
        closeFunc()
      }
    },
    scrollTop () {
      this.$refs.dialogCard.$el.scrollTop = 0
    },
    scrollBottom () {
      this.$nextTick(() => {
        try {
          this.$refs.dialogCard.$el.scrollTop = this.$refs.dialogCard.$el.children[0].clientHeight
        }
        catch (err) {}
      })
    },
    onShowDialog () {
      if (Array.isArray(this.tags) && this.tags.length > 0) {
        this.$bus.emit('help:push', this.tags)
      }
      if (!this.skipHelpChecks) {
        this.$bus.emit('help:rerender')
      }
      this.$emit('show')
    },
    onHideDialog () {
      if (Array.isArray(this.tags) && this.tags.length > 0) {
        this.$bus.emit('help:pop')
      }
      this.$emit('hide')
    }
  },
  beforeUnmount () {
    this.removeBackListener()
  }
}
</script>
